import React from 'react';
import { motion } from 'framer-motion';
import {
  Droplets,
  Factory,
  Leaf,
  Recycle,
} from 'lucide-react';
import { useCountUp } from '../../hooks/useCountUp';
import {
  homeScrollItem,
  homeScrollStagger,   
  homeScrollViewport,
} from '../../utils/homeScrollMotion';
import styles from './SustainabilityCommitmentSection.module.css';

const pillars = [
  {
    icon: Leaf,
    value: 85,
    title: 'Eco-friendly materials',
    detail: 'FSC-certified fibre and responsible procurement across our supply chain.',
  },
  {
    icon: Recycle,
    value: 40,
    title: 'Recycling programs',
    detail: 'Circular supply chain and post-use recovery programs.',
  },
  {
    icon: Factory,
    value: 90,
    title: 'Green manufacturing',
    detail: 'Energy-efficient upgrades and cleaner processes at scale.',
  },
  {
    icon: Droplets,
    value: 100,
    title: 'Water conservation',
    detail: 'Recirculation on the production floor — zero waste discharge.',
  },
];

function MetricCounter({ value, delay = 0 }) {
  const { ref, value: display } = useCountUp(value, {
    duration: 1500,
    delay,
  });

  return (
    <p className={styles.metric} ref={ref}>
      {display}%
    </p>
  );
}

const SustainabilityCommitmentSection = () => (
  <section
    className={`${styles.section} home-bg-linen`}
    aria-labelledby="impact-framework-title"
  >
    <div className={styles.bgDecor} aria-hidden="true">
      <span className={styles.orbGreen} />
      <span className={styles.orbOrange} />
    </div>
    <div className={styles.container}>
      <motion.header
        className={styles.header}
        variants={homeScrollItem}
        initial="hidden"
        whileInView="visible"
        viewport={homeScrollViewport}
      >
        <p className={styles.kicker}>Greeneri Impact Framework</p>
        <h2 id="impact-framework-title" className={styles.title}>
          Our <span className={styles.highlight}>Sustainability Commitment</span>
        </h2>
        <p className={styles.subtitle}>
          Circular production and responsible manufacturing — measurable impact from Meerut across India.
        </p>
      </motion.header>

      <motion.ul
        className={styles.grid}
        variants={homeScrollStagger}
        initial="hidden"
        whileInView="visible"
        viewport={homeScrollViewport}
      >
        {pillars.map((pillar, index) => {
          const Icon = pillar.icon;

          return (
            <motion.li key={pillar.title} className={styles.item} variants={homeScrollItem}>
              <MetricCounter value={pillar.value} delay={index * 120} />
              <h3 className={styles.itemTitle}>
                <Icon size={14} strokeWidth={2.3} aria-hidden="true" />
                {pillar.title}
              </h3>
              <p className={styles.itemDetail}>{pillar.detail}</p>
            </motion.li>
          );
        })}
      </motion.ul>

      <motion.p
        className={styles.footnote}
        variants={homeScrollItem}
        initial="hidden"
        whileInView="visible"
        viewport={homeScrollViewport}
      >
        Every metric tracked on the factory floor — fibre sourcing to finished roll.
      </motion.p>
    </div>
  </section>
);

export default SustainabilityCommitmentSection;
